import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../services/apiClient';

// 👍 👎 ‼️ ❤️ 😢 😂 under a daily update or a shop note (Pat, Sep 2026) — a one-tap
// "I saw this", so nobody has to type "ok". One reaction per person per post: tap a
// chip to add yours or take it back, tap the smiley to pick (or swap to) another, tap
// a chip's count to see who. The server keeps them by key (up / down / bang …); the
// pictures live here. The writer hears about it in the bell — nothing is emailed.
export const REACTIONS = [
  { key: 'up', emoji: '👍', label: 'Thumbs up' },
  { key: 'down', emoji: '👎', label: 'Thumbs down' },
  { key: 'bang', emoji: '‼️', label: 'Important' },
  { key: 'heart', emoji: '❤️', label: 'Heart' },
  { key: 'cry', emoji: '😢', label: 'Sad' },
  { key: 'laugh', emoji: '😂', label: 'Ha ha' },
];
const EMOJI = Object.fromEntries(REACTIONS.map(r => [r.key, r.emoji]));

function myId() {
  try {
    const s = JSON.parse(localStorage.getItem('titan_session') || 'null');
    const w = JSON.parse(localStorage.getItem('te_worker') || 'null');
    return String(s?.userId || s?.user?.id || w?.id || 'admin');
  } catch { return 'admin'; }
}

// type: 'update' | 'activity' · id: that row's id · reactions: what the server sent
// with the row (so a list of fifty updates is not fifty requests).
export default function Reactions({ type, id, reactions = [], onDark = false, style }) {
  const [list, setList] = useState(Array.isArray(reactions) ? reactions : []);
  const [picking, setPicking] = useState(false);
  const [who, setWho] = useState(null);      // the reaction key whose names are showing
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  useEffect(() => { setList(Array.isArray(reactions) ? reactions : []); }, [reactions]);
  // A tap anywhere else closes the picker / the names.
  useEffect(() => {
    if (!picking && !who) return undefined;
    const away = e => { if (box.current && !box.current.contains(e.target)) { setPicking(false); setWho(null); } };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [picking, who]);

  if (!type || !id) return null;
  const me = myId();
  const mine = list.find(r => String(r.user_id) === me)?.emoji || null;
  const groups = REACTIONS.map(r => ({ ...r, people: list.filter(x => x.emoji === r.key) })).filter(g => g.people.length > 0);

  async function react(key) {
    if (busy) return;
    setBusy(true); setPicking(false); setWho(null);
    try {
      const r = await apiFetch('/reactions', { method: 'PUT', body: JSON.stringify({ target_type: type, target_id: id, emoji: key }) });
      setList(Array.isArray(r?.reactions) ? r.reactions : []);
    } catch (e) { window.alert(e?.error || e?.message || 'That reaction did not save — try again.'); }
    setBusy(false);
  }

  const ink = onDark ? '#fff' : '#374151';
  const chip = on => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, height: 26, padding: '0 8px', borderRadius: 999, cursor: 'pointer',
    border: `1px solid ${on ? '#93c5fd' : (onDark ? 'rgba(255,255,255,0.35)' : '#e5e7eb')}`,
    background: on ? '#eff6ff' : (onDark ? 'rgba(255,255,255,0.12)' : '#fff'),
    color: on ? '#1d4ed8' : ink, fontSize: 12.5, fontWeight: 700, lineHeight: 1, fontFamily: 'inherit',
  });

  return (
    <div ref={box} onClick={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginTop: 6, ...style }}>
      {groups.map(g => (
        <span key={g.key} style={{ position: 'relative', display: 'inline-flex' }}>
          <button type="button" disabled={busy} onClick={() => react(g.key)} title={g.people.map(p => p.user_name || 'Someone').join(', ')}
            style={{ ...chip(mine === g.key), borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none', paddingRight: 5 }}>
            <span style={{ fontSize: 14 }}>{g.emoji}</span>
          </button>
          <button type="button" onClick={() => { setPicking(false); setWho(w => (w === g.key ? null : g.key)); }} aria-label={`Who reacted ${g.label}`}
            style={{ ...chip(mine === g.key), borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 'none', paddingLeft: 3 }}>
            {g.people.length}
          </button>
          {who === g.key && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, background: '#111827', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 600, lineHeight: 1.5, whiteSpace: 'nowrap', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
              {g.people.map(p => <div key={p.user_id}>{g.emoji} {p.user_name || 'Someone'}</div>)}
            </div>
          )}
        </span>
      ))}
      <button type="button" disabled={busy} onClick={() => { setWho(null); setPicking(v => !v); }} aria-label="React" title={mine ? 'Change your reaction' : 'React'}
        style={{ ...chip(false), padding: '0 7px', color: onDark ? 'rgba(255,255,255,0.8)' : '#9ca3af', fontWeight: 600 }}>
        <span style={{ fontSize: 14, filter: 'grayscale(1)', opacity: 0.75 }}>🙂</span><span style={{ fontSize: 13 }}>+</span>
      </button>
      {picking && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50, display: 'flex', gap: 2, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '4px 6px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
          {REACTIONS.map(r => (
            <button key={r.key} type="button" onClick={() => react(r.key)} title={mine === r.key ? `${r.label} — tap to take it back` : r.label} aria-label={r.label}
              style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 21, lineHeight: 1, background: mine === r.key ? '#dbeafe' : 'transparent' }}>
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
export { EMOJI as REACTION_EMOJI };
